"use client";

import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectCard } from "@/components/ProjectCard";
import { X, Upload, Image } from "lucide-react";
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay
} from '@dnd-kit/core';

export function StackSettingsModal({
  isOpen,
  onClose,
  projects: initialProjects,
  stackName: initialStackName,
  stackImage: initialStackImage,
  stackId,
  onSave,
  onEmptyStack,
  onDisband,
  onRemoveProjectFromStack
}) {
  const [stackName, setStackName] = useState(initialStackName || "New stack");
  const [selectedImage, setSelectedImage] = useState(initialStackImage || null);
  const [projects, setProjects] = useState(initialProjects || []);
  const [activeId, setActiveId] = useState(null);
  const modalRef = useRef(null);
  const isEditMode = !!initialStackName; // If we have an initial name, we're editing

  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Update form state when modal opens with new stack data
  useEffect(() => {
    if (isOpen) {
      setStackName(initialStackName || "New stack");
      setSelectedImage(initialStackImage || null);
      setProjects(initialProjects || []);
    }
  }, [isOpen, initialStackName, initialStackImage, initialProjects]);

  const handleSave = () => {
    onSave({
      name: stackName,
      image: selectedImage,
      projects: projects
    });
    onClose();
  };

  const handleNeverMind = () => {
    onClose();
  };

  const handleRemoveProject = (projectId) => {
    // Immediately remove from parent state
    if (isEditMode && stackId && onRemoveProjectFromStack) {
      const projectToRemove = projects.find(p => p.id === projectId);
      if (projectToRemove) {
        onRemoveProjectFromStack(stackId, projectToRemove);
      }
    }
    // Update local state to reflect the change
    setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
  };

  const handleDisbandStack = () => {
    onDisband?.();
    onClose();
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over && modalRef.current) {
      // Card was dropped outside the modal - remove it immediately
      const draggedProjectId = active.id;
      handleRemoveProject(draggedProjectId);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent ref={modalRef} className="!max-w-5xl w-[90vw] p-6 sm:!max-w-5xl">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-6">
          {/* Image Upload Section */}
          <div className="flex flex-col items-center space-y-3">
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
              {selectedImage ? (
                <img 
                  src={selectedImage} 
                  alt="Stack preview" 
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <Image className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <div>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <label
                htmlFor="image-upload"
                className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer underline"
              >
                Choose an image
              </label>
            </div>
          </div>

          {/* Stack Name Input - Editable title */}
          <div className="flex justify-center">
            <input
              type="text"
              value={stackName}
              onChange={(e) => setStackName(e.target.value)}
              className="text-center text-2xl font-semibold border-none outline-none focus:outline-none bg-transparent hover:bg-gray-50 focus:bg-gray-50 px-4 py-2 rounded transition-colors"
              placeholder="Stack name"
            />
          </div>

          {/* Projects Preview - Using actual ProjectCard component */}
          <div className="flex justify-center">
            {projects && projects.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-4 w-full max-w-7xl">
                {projects.map((project) => (
                  <div key={project.id} className="w-[calc(50%-8px)] sm:w-[300px]">
                    <ProjectCard
                      project={project}
                      onColorChange={() => {}}
                      onPin={null}
                      onRemoveFromStack={isEditMode ? () => handleRemoveProject(project.id) : null}
                      isDragActive={activeId === project.id}
                      isDropTarget={false}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <div className="text-center py-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">Empty Stack</h3>
                  <p className="text-sm text-gray-500">No spaces in this stack</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons - Disband on left, Cancel/Save on right */}
          <div className="flex justify-between items-center pt-4">
            {/* Disband/Delete Stack button - only show in edit mode */}
            {isEditMode ? (
              <Button
                variant="destructive"
                onClick={handleDisbandStack}
              >
                {projects && projects.length > 0 ? 'Disband Stack' : 'Delete Stack'}
              </Button>
            ) : (
              <div /> // Empty spacer when not in edit mode
            )}

            {/* Cancel and Save buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleNeverMind}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Drag Overlay for smooth drag preview */}
        <DragOverlay>
          {activeId ? (
            <div className="opacity-80 rotate-3 scale-110">
              <ProjectCard
                project={projects.find(p => p.id === activeId)}
                onColorChange={() => {}}
                onPin={null}
                isDragActive={false}
                isDropTarget={false}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      </DialogContent>
    </Dialog>
  );
}